import {
  resolveUploadThumbSrc,
  toCanonicalUploadsPath,
} from '@/tiktok-platform/modules/products/components/publishImageUploadThumbSrc';

function isEphemeralUrl(url) {
  const u = String(url || '').trim().toLowerCase();
  return u.startsWith('blob:') || u.startsWith('data:');
}

/**
 * 单槽位规范化：
 * - 绝对 localhost:端口/uploads → canonical /uploads/…
 * - 失效 blob/data 一律换成 durable（回填后不能再喂灰块）
 * - 仅测网 CDN / 无法预览 → null
 */
function normalizeImageSlot(img) {
  if (!img || typeof img !== 'object') return img;
  const durable = toCanonicalUploadsPath(img.ipeUrl)
    || toCanonicalUploadsPath(img.publicUrl)
    || toCanonicalUploadsPath(img.url)
    || toCanonicalUploadsPath(img.previewUrl)
    || '';
  if (!durable) {
    // 仅测网 CDN / 失效地址：清掉，避免灰块/裂图占槽
    if (!resolveUploadThumbSrc(img)) return null;
    const preview = String(img.previewUrl || '').trim();
    if (isEphemeralUrl(preview)) {
      // 无 durable 的死 blob：清空
      return null;
    }
    return img;
  }
  const tiktokUrl = String(img.tiktokUrl || '').trim();
  return {
    ...img,
    ipeUrl: durable,
    publicUrl: durable,
    url: durable,
    // 禁止把 blob:/data: 写回表单：刷新或隔日打开必灰
    previewUrl: durable,
    ...(tiktokUrl ? { tiktokUrl } : {}),
  };
}

function mapImageArray(list) {
  if (!Array.isArray(list)) return { next: list, changed: false };
  let changed = false;
  const next = list.map((img) => {
    const n = normalizeImageSlot(img);
    if (n !== img) changed = true;
    return n;
  }).filter(Boolean);
  if (next.length !== list.length) changed = true;
  return { next, changed };
}

function normalizeShowroomSlots(slots) {
  if (!slots || typeof slots !== 'object') {
    return { next: slots, changed: false };
  }
  let changed = false;
  const next = { ...slots };
  Object.keys(slots).forEach((key) => {
    const n = normalizeImageSlot(slots[key]);
    if (n !== slots[key]) {
      changed = true;
      next[key] = n;
    }
  });
  return { next, changed };
}

function slotSubmitUrl(img) {
  if (!img || typeof img !== 'object') return '';
  return String(
    img.tiktokUrl
    || img.publicUrl
    || img.ipeUrl
    || img.url
    || img.previewUrl
    || '',
  ).trim();
}

/**
 * 编辑回填 / 持久化前统一清洗图片槽：
 * - 绝对 localhost:端口/uploads → canonical /uploads/…
 * - 去掉失效 blob/data
 * - 无法预览的测网空壳 → null（显示点击上传）
 * - 素材图 materialImage 与 SKC 图同一规则（否则草稿重开必裂图）
 */
export function sanitizeTiktokFormImageSlots(form) {
  if (!form || typeof form !== 'object') return form;
  const skcList = Array.isArray(form.skcList) ? form.skcList : null;
  const printImages = Array.isArray(form.printImages) ? form.printImages : null;
  const certifications = Array.isArray(form.certifications) && form.certifications.length
    ? form.certifications
    : null;
  const hasMaterialSlot = Object.prototype.hasOwnProperty.call(form, 'materialImage');
  const hasOuterPkgSlot = Object.prototype.hasOwnProperty.call(form, 'outerPackageImages');
  const hasSizeChartImageSlot = Object.prototype.hasOwnProperty.call(form, 'sizeChartImage');
  const hasProductMainSlot = Object.prototype.hasOwnProperty.call(form, 'mainImage');
  const hasProductDetailSlot = Object.prototype.hasOwnProperty.call(form, 'detailImages');
  if (!skcList && !printImages && !certifications && !hasMaterialSlot
    && !hasOuterPkgSlot && !hasSizeChartImageSlot
    && !hasProductMainSlot && !hasProductDetailSlot) {
    return form;
  }

  let changed = false;

  // 商品级主图 / 细节图（不按颜色分）
  let nextProductMain = form.mainImage;
  if (hasProductMainSlot) {
    const n = normalizeImageSlot(form.mainImage);
    if (n !== form.mainImage) {
      changed = true;
      nextProductMain = n;
    }
  }
  let nextProductDetails = form.detailImages;
  if (hasProductDetailSlot) {
    const mapped = mapImageArray(
      Array.isArray(form.detailImages) ? form.detailImages : [],
    );
    nextProductDetails = mapped.next;
    if (mapped.changed) changed = true;
  }

  let nextPrint = printImages;
  if (printImages) {
    const mapped = mapImageArray(printImages);
    nextPrint = mapped.next;
    if (mapped.changed) changed = true;
  }

  let nextOuterPkg = form.outerPackageImages;
  if (hasOuterPkgSlot) {
    const mapped = mapImageArray(
      Array.isArray(form.outerPackageImages) ? form.outerPackageImages : [],
    );
    nextOuterPkg = mapped.next;
    if (mapped.changed) changed = true;
  }

  let nextCertifications = certifications;
  if (certifications) {
    nextCertifications = certifications.map((row) => {
      if (!row || typeof row !== 'object') return row;
      if (!Array.isArray(row.images)) return row;
      const mapped = mapImageArray(row.images);
      if (!mapped.changed) return row;
      changed = true;
      return { ...row, images: mapped.next };
    });
  }

  let nextMaterial = form.materialImage;
  let nextMaterialUrl = form.materialImgUrl;
  if (hasMaterialSlot) {
    const n = normalizeImageSlot(form.materialImage);
    if (n !== form.materialImage) {
      changed = true;
      nextMaterial = n;
      if (n) {
        nextMaterialUrl = slotSubmitUrl(n) || String(form.materialImgUrl || '').trim();
      } else {
        const kept = String(form.materialImgUrl || '').trim();
        nextMaterialUrl = (kept && !isEphemeralUrl(kept)) ? kept : '';
      }
    } else if (isEphemeralUrl(form.materialImgUrl)) {
      changed = true;
      nextMaterialUrl = slotSubmitUrl(n) || '';
    }
  }

  // 尺码表图片（SIZE_CHART_IMAGE）：草稿只持久化可浏览地址
  let nextSizeChartImage = form.sizeChartImage;
  if (hasSizeChartImageSlot) {
    const n = normalizeImageSlot(form.sizeChartImage);
    if (n !== form.sizeChartImage) {
      changed = true;
      nextSizeChartImage = n;
    }
  }

  let nextSkcList = skcList;
  if (skcList) {    nextSkcList = skcList.map((skc) => {
      if (!skc || typeof skc !== 'object') return skc;
      let skcChanged = false;
      let next = skc;

      const mainImage = normalizeImageSlot(skc.mainImage);
      if (mainImage !== skc.mainImage) {
        skcChanged = true;
        next = { ...next, mainImage };
      }

      const squareImage = normalizeImageSlot(skc.squareImage);
      if (squareImage !== skc.squareImage) {
        skcChanged = true;
        next = { ...next, squareImage };
      }

      const colorBlockImage = normalizeImageSlot(skc.colorBlockImage);
      if (colorBlockImage !== skc.colorBlockImage) {
        skcChanged = true;
        next = { ...next, colorBlockImage };
      }

      // SKU 变体图（ATTRIBUTE_IMAGE，挂色块）：草稿只持久化可浏览地址（TK-PUB-27）
      const skuImage = normalizeImageSlot(skc.skuImage);
      if (skuImage !== skc.skuImage) {
        skcChanged = true;
        next = { ...next, skuImage };
      }

      if (Array.isArray(skc.detailImages)) {
        const mapped = mapImageArray(skc.detailImages);
        if (mapped.changed) {
          skcChanged = true;
          next = { ...next, detailImages: mapped.next };
        }
      }

      const showroom = normalizeShowroomSlots(skc.showroomSlots);
      if (showroom.changed) {
        skcChanged = true;
        next = { ...next, showroomSlots: showroom.next };
      }

      if (Array.isArray(skc.skuRows)) {
        const skuRows = skc.skuRows.map((sku) => {
          if (!sku || !sku.skuImage) return sku;
          const skuImage = normalizeImageSlot(sku.skuImage);
          if (skuImage === sku.skuImage) return sku;
          skcChanged = true;
          return { ...sku, skuImage };
        });
        if (skcChanged) next = { ...next, skuRows };
      }

      if (skcChanged) changed = true;
      return next;
    });
  }

  if (!changed) return form;
  return {
    ...form,
    ...(hasProductMainSlot ? { mainImage: nextProductMain } : {}),
    ...(hasProductDetailSlot ? { detailImages: nextProductDetails } : {}),
    ...(nextPrint ? { printImages: nextPrint } : {}),
    ...(nextSkcList ? { skcList: nextSkcList } : {}),
    ...(nextCertifications ? { certifications: nextCertifications } : {}),
    ...(hasMaterialSlot ? {
      materialImage: nextMaterial,
      materialImgUrl: nextMaterialUrl || '',
    } : {}),
    ...(hasOuterPkgSlot ? {
      outerPackageImages: nextOuterPkg,
    } : {}),
    ...(hasSizeChartImageSlot ? {
      sizeChartImage: nextSizeChartImage,
    } : {}),
  };
}

/** 模板/草稿落库：与 sanitize 同规则，显式命名强调「禁止持久化 blob」。 */
export function persistTiktokFormImageSlots(form) {
  return sanitizeTiktokFormImageSlots(form);
}
